import React, { useEffect, useState } from 'react';
import { Button, ButtonGroup, Checkbox, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import { Cell, Legend, Pie, PieChart, Tooltip } from 'recharts';
import apiService from '../../api/apiService';

const hazardRiskScore = {
    LOW: 4,
    MEDIUM: 10,
    HIGH: 16,
};

const DashboardRiskLevel = ({ jsonSettings, onSettingsChange }) => {
    const [riskData, setRiskData] = useState([]);

    const getDefaultDateRange = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 1);
        return {
            start: startDate.toISOString().split('T')[0],
            end: endDate.toISOString().split('T')[0]
        };
    };

    const parseSettings = (settings) => {
        try {
            if (!settings || settings === '{}')
                return {};

            return typeof settings === 'string' ? JSON.parse(settings) : settings;
        }
        catch (error) {
            console.error('Error parsing jsonSettings:', error);
            return {};
        }
    };

    const settings = parseSettings(jsonSettings);

    // Initialize state from jsonSettings
    const [isControlled, setIsControlled] = useState(settings.isControlled ?? false);
    const [dateRangeEnabled, setDateRangeEnabled] = useState(settings.dateRangeEnabled ?? false);
    const [dateRange, setDateRange] = useState(settings.dateRange ?? getDefaultDateRange());

    const COLORS = {
        'Low': '#82ca9d',
        'Medium': '#ffc658',
        'High': '#ffa726',
        'Very High': '#d88487'
    };

    useEffect(() => {
        void loadHazardData();
    }, [isControlled, dateRange, dateRangeEnabled]);

    const loadHazardData = async () => {
        try {
            const url = dateRangeEnabled
                ? `api/Hazard?startDate=${dateRange.start}&endDate=${dateRange.end}`
                : 'api/Hazard';
            const response = await apiService.getData(url);
            const hazardData = response.data.data;

            const likelihoodResponse = await apiService.getData('api/Likelihood');
            const severityResponse = await apiService.getData('api/Severity');

            const likelihoodList = likelihoodResponse.data.data;
            const severityList = severityResponse.data.data;

            const riskLevels = { 'Low': 0, 'Medium': 0, 'High': 0, 'Very High': 0 };

            hazardData.forEach((hazard) => {
                const likelihood = likelihoodList.find(l => l.lkId === hazard.hzLikelihood)?.lkFactor || hazard.hzLikelihood;
                const severity = severityList.find(s => s.svId === hazard.hzSeverity)?.svFactor || hazard.hzSeverity;

                let riskScore = severity * likelihood;

                if (isControlled && hazard.hazardControlDtos && hazard.hazardControlDtos.length > 0) {
                    hazard.hazardControlDtos.forEach(hazardControl => {
                        const controlFactor = hazardControl.hcControlTypeNavigation?.ctFactor || 1;
                        riskScore = riskScore * controlFactor;
                    });
                }

                const riskLevel = getRiskScoreValue(riskScore);
                riskLevels[riskLevel]++;
            });

            const chartData = Object.entries(riskLevels)
                .filter(([_, count]) => count > 0)
                .map(([name, count]) => ({ name, count }));

            setRiskData(chartData);
        }
        catch (error) {
            console.error('Error loading hazard data:', error);
        }
    };

    const getRiskScoreValue = (value) => {
        if (value < hazardRiskScore.LOW) {
            return 'Low';
        }
        else if (value < hazardRiskScore.MEDIUM) {
            return 'Medium';
        }
        else if (value < hazardRiskScore.HIGH) {
            return 'High';
        }
        else {
            return 'Very High';
        }
    };

    const saveSettings = (startDate, endDate, isControlled, dateRangeEnabled) => {
        if (onSettingsChange) {
            const dateRange = { start: startDate, end: endDate };

            const newSettings = {
                dateRangeEnabled,
                isControlled,
                dateRange
            };

            onSettingsChange(JSON.stringify(newSettings));
        }
    }

    const onStartDateChange = (newStartDate) => {
        setDateRange({ ...dateRange, start: newStartDate });
        saveSettings(newStartDate, dateRange.end, isControlled, dateRangeEnabled);
    }

    const onEndDateChange = (newEndDate) => {
        setDateRange({ ...dateRange, end: newEndDate });
        saveSettings(dateRange.start, newEndDate, isControlled, dateRangeEnabled);
    }

    const onDateRangeEnabledChange = (newDateRangeEnabled) => {
        setDateRangeEnabled(newDateRangeEnabled);
        saveSettings(dateRange.start, dateRange.end, isControlled, newDateRangeEnabled);
    }

    const onIsControlledChange = (newIsControlled) => {
        setIsControlled(newIsControlled);
        saveSettings(dateRange.start, dateRange.end, newIsControlled, dateRangeEnabled);
    }

    return (
        <Grid container direction='column' justifyContent='center' alignItems='center' style={{ height: '100%', overflow: 'auto' }}>
            <Grid item>
                <Typography variant='h6'>Hazard Risk Levels</Typography>
            </Grid>
            <Grid item style={{ marginBottom: '10px' }}>
                <Grid container spacing={2} alignItems='center' direction='column'>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={dateRangeEnabled}
                                    onChange={(e) => onDateRangeEnabledChange(e.target.checked)}
                                    size='small'
                                />
                            }
                            label='Filter by date range'
                        />
                    </Grid>
                    <Grid item>
                        <Grid container spacing={2} alignItems='center'>
                            <Grid item>
                                <TextField
                                    label='Start Date'
                                    type='date'
                                    value={dateRange.start}
                                    onChange={(e) => onStartDateChange(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    size='small'
                                    disabled={!dateRangeEnabled}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    label='End Date'
                                    type='date'
                                    value={dateRange.end}
                                    onChange={(e) => onEndDateChange(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    size='small'
                                    disabled={!dateRangeEnabled}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item style={{ marginBottom: '10px' }}>
                <ButtonGroup variant='contained' size='small'>
                    <Button
                        onClick={() => onIsControlledChange(false)}
                        color={!isControlled ? 'primary' : 'inherit'}
                    >
                        Uncontrolled
                    </Button>
                    <Button
                        onClick={() => onIsControlledChange(true)}
                        color={isControlled ? 'primary' : 'inherit'}
                    >
                        Controlled
                    </Button>
                </ButtonGroup>
            </Grid>
            <Grid item style={{ width: 600, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {riskData.length === 0 ? (
                    <Typography variant='body1' color='textSecondary'>
                        {dateRangeEnabled ? 'No hazards found for the selected date range' : 'No hazards found'}
                    </Typography>
                ) : (
                    <PieChart width={600} height={400}>
                        <Pie
                            dataKey='count'
                            data={riskData}
                            cx='50%'
                            cy='50%'
                            outerRadius={80}
                            fill='#8884d8'
                            label={({ name, count }) => `${name}: ${count}`}
                            isAnimationActive={false}
                        >
                            {riskData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[entry.name]} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                )}
            </Grid>
        </Grid>
    );
};
export default DashboardRiskLevel;
